import * as React from 'react';
import Typography from '@mui/material/Typography';
import { AppCTX } from '../data/AppContext';
import List from '@mui/material/List';
import IconButton from '@mui/material/IconButton';
import PinDropIcon from '@mui/icons-material/PinDrop';
import CallIcon from '@mui/icons-material/Call';
import DeleteIcon from '@mui/icons-material/Delete';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export default function ProspectsList({ 
  showDetails, 
  setShowDetails, 
  selectedProspect, 
  setSelectedProspect, 
  prospects 
}) {
  const { map, deleteProspect, setStatus } = React.useContext(AppCTX);
  
  // State for scroll indicator
  const [showScrollIndicator, setShowScrollIndicator] = React.useState(false);
  const [isAtBottom, setIsAtBottom] = React.useState(true);
  const [isScrolling, setIsScrolling] = React.useState(false);
  const scrollContainerRef = React.useRef(null);
  const scrollTimeoutRef = React.useRef(null);

  // Check scroll position to determine if we're at the bottom
  const checkScrollPosition = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
    const hasMoreContent = scrollHeight > clientHeight;
    
    setIsAtBottom(isBottom);
    setShowScrollIndicator(hasMoreContent && !isBottom);
  }, []);

  // Handle scroll events with debouncing
  const handleScroll = React.useCallback(() => {
    checkScrollPosition();
    
    // Show scrolling state
    setIsScrolling(true);
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Hide scrolling state after 150ms of no scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  }, [checkScrollPosition]);

  // Set up scroll listener
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition(); // Check initial position
    
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkScrollPosition);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [checkScrollPosition, handleScroll, prospects.length]);

  // Handle prospect selection
  const handleProspectClick = (prospect) => {
    setSelectedProspect(prospect);
    setShowDetails(true);
  };

  // Handle location navigation
  const handleLocationClick = (prospect, event) => {
    event.stopPropagation(); // Prevent triggering the parent click
    if (prospect.coords && map) {
      navigator.clipboard.writeText(prospect.address);
      map.panTo(prospect.coords);
      setStatus({ notificationType: 'success', message: 'Address copied to clipboard' });
    }
  };

  // Handle phone call
  const handleCallClick = (prospect, event) => {
    event.stopPropagation(); // Prevent triggering the parent click
    // The href will handle the call automatically
  };

  // Handle prospect deletion
  const handleDeleteClick = (prospect, event) => {
    event.stopPropagation(); // Prevent triggering the parent click
    if (prospect.address) {
      deleteProspect(prospect.address);
    }
  };

  // Render individual prospect item
  const renderProspectItem = (prospect) => {
    // Skip unsaved prospects (those without setter field)
    if (!prospect.setter) {
      return null;
    }

    // Skip prospects without owners
    if (!prospect.owners || !Array.isArray(prospect.owners) || prospect.owners.length === 0) {
      return null;
    }

    const primaryContact = prospect.owners[0];
    const secondaryContact = prospect.owners.length > 1 ? prospect.owners[1] : null;

    // Skip if primary contact is missing
    if (!primaryContact || !primaryContact.name) {
      return null;
    }

    // Get the latest note
    const latestNote = prospect.notes && prospect.notes.length > 0 
      ? prospect.notes[prospect.notes.length - 1] 
      : 'No notes';

    // Format contact names
    const contactNames = secondaryContact && secondaryContact.name 
      ? `${primaryContact.name} & ${secondaryContact.name}`
      : primaryContact.name;

    return (
      <Box key={`${primaryContact.name}-${primaryContact.number || 'no-number'}`} sx={{ mb: 2 }}>
        <Paper
          elevation={2}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderRadius: 3,
            background: '#232323',
            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            minHeight: 72,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': { 
              background: '#282828',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
            '&:active': {
              transform: 'translateY(0px)',
              transition: 'all 0.1s ease-in-out',
            },
          }}
          onClick={() => handleProspectClick(prospect)}
        >
          {/* Prospect Information */}
          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: '#fff', 
                fontWeight: 600, 
                fontSize: 18, 
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
              }}
            >
              {contactNames}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#bbb', 
                fontSize: 15, 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
              }}
            >
              {latestNote}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <IconButton 
              onClick={(e) => handleLocationClick(prospect, e)} 
              aria-label="location" 
              sx={{ 
                color: '#90caf9',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { 
                  background: 'rgba(144, 202, 249, 0.1)',
                  transform: 'scale(1.1)'
                },
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <PinDropIcon fontSize="medium" />
            </IconButton>
            
            <IconButton 
              href={`tel:${primaryContact.number || ''}`} 
              onClick={(e) => handleCallClick(prospect, e)}
              aria-label="call" 
              sx={{ 
                color: '#81c784',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { 
                  background: 'rgba(129, 199, 132, 0.1)',
                  transform: 'scale(1.1)'
                },
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <CallIcon fontSize="medium" />
            </IconButton>
            
            <IconButton 
              onClick={(e) => handleDeleteClick(prospect, e)} 
              aria-label="delete" 
              sx={{ 
                color: '#e57373',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { 
                  background: 'rgba(229, 115, 115, 0.1)',
                  transform: 'scale(1.1)'
                },
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <DeleteIcon fontSize="medium" />
            </IconButton>
          </Box>
        </Paper>
      </Box>
    );
  };

  // Render empty state
  const renderEmptyState = () => (
    <Box sx={{ 
      p: 3, 
      textAlign: 'center',
      color: '#bbb',
      maxWidth: '600px', // Match the list max-width
      mx: 'auto', // Center the empty state
    }}>
      <Typography variant="subtitle1" gutterBottom>
        To add a prospect, pick a house by tapping on the map. 
        A button with the text "Add prospect" will appear on the right side of the map. 
        Tap this button to start entering prospect details and add them to your list.
      </Typography>
    </Box>
  );

  // Render scroll indicator
  const renderScrollIndicator = () => {
    if (!showScrollIndicator) return null;

    return (
      <Box
        sx={{
          position: 'fixed', // Fixed positioning at bottom of screen
          bottom: 80, // Position above the bottom navigation (assuming nav is ~60px + some padding)
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          pointerEvents: 'none', // Don't interfere with scrolling
          opacity: isScrolling ? 0.3 : 1, // Fade out when scrolling
          transition: 'opacity 0.2s ease-in-out',
          // Background blur for better visibility
          backdropFilter: 'blur(8px)',
          background: 'rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          px: 1.5,
          py: 0.5,
        }}
      >
        {/* Gradient overlay to fade the indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 60,
            height: 20,
            background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.3))',
            borderRadius: '50%',
            filter: 'blur(8px)',
          }}
        />
        
        {/* Arrow icon */}
        <KeyboardArrowDownIcon
          sx={{
            color: '#90caf9',
            fontSize: 24,
            opacity: 0.8,
            animation: isScrolling ? 'none' : 'bounce 2s infinite',
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': {
                transform: 'translateY(0)',
              },
              '40%': {
                transform: 'translateY(-4px)',
              },
              '60%': {
                transform: 'translateY(-2px)',
              },
            },
          }}
        />
        
        {/* Text indicator */}
        <Typography
          variant="caption"
          sx={{
            color: '#90caf9',
            fontSize: '0.75rem',
            fontWeight: 500,
            opacity: 0.8,
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          }}
        >
          More leads below
        </Typography>
      </Box>
    );
  };

  return (
    <Box 
      ref={scrollContainerRef}
      sx={{ 
        height: '100%', 
        overflow: 'auto',
        position: 'relative', // For positioning the scroll indicator
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#555',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#777',
        }
      }}
    >
      <List sx={{ 
        width: '100%', 
        p: 2,
        maxWidth: '600px', // Prevent cards from stretching too wide
        mx: 'auto', // Center the list on larger screens
      }}>
        {prospects.length === 0 ? renderEmptyState() : prospects.map(renderProspectItem)}
      </List>
      
      {/* Scroll indicator */}
      {renderScrollIndicator()}
    </Box>
  );
}
